import React, { useMemo, useState } from "react";
import { showError, showSuccess } from "toast/toast";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  createColumnHelper,
  type ColumnDef,
} from "@tanstack/react-table";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { SquarePen, Package } from 'lucide-react';
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import {
  createVolumeDiscountTemplate,
  createVolumeDiscountAssignment,
  type VolumeTemplateCreatePayload,
  getAssignmentsDatatable,
  updateVolumeDiscountTemplates,
  updateVolumeDiscountAssignment,
  deleteVolumeDiscountAssignment,
  type AssignmentsDatatableQuery,
} from "components/volumeDiscountTemplates"


// ===== Types (UI-only) =====
export type ProductType = "simple" | "variable";

export type VolumeDiscountCombination = {
  id?: string;
  label?: string;
  attributes?: Record<string, string>;
  name?: string;    // optional
  sku?: string;     // optional
};


export type VolumeDiscountUI = {
  _id?: string;                      // optional, assignment id
  id: string;                         // stable client id
  combinationId: "all" | string;     // "all" or specific variant id
  startingAt: number;                 // quantity break
  leaveInitialPrice: boolean;         // when false => fixed price flow
  fixedPrice?: number | null;         // required when leaveInitialPrice = false
  discountValue?: number | null;      // required when leaveInitialPrice = true
  discountIn?: "%" | "$";          // required when leaveInitialPrice = true
  templateId?: string;                // optional
  discountId?: string;           // optional
};

type ComboForDiscount = {
  id: string;             // works with your combinationsfetch.id
  variant_id?: string;    // optional, if provided
  variant_name?: string;
  sku?: string;
  attributes?: Record<string, any>;
};

type Props = {
  productType: "variable" | "simple";
  combinations?: ComboForDiscount[];
  value?: VolumeDiscountUI[];
  onChange?: (rows: VolumeDiscountUI[]) => void;
  productId: string;            // required
  token?: string | null;        // optional
  simpleVariantId?: string;
};

type AnyAssignment = {
  _id?: string;
  template_id?: string;
  product_id?: string;
  variant_id?: string | null;
  discount_id?: string;
  apply_to_all_variants?: boolean;
  status?: string;
  priority?: number;
  // sometimes template fields are flattened:
  starting_at?: number;
  discount?: number | null;
  discount_in?: "%" | "$" | null;
  fixed_price?: number | null;
  // or nested template:
  template?: {
    id?: string;
    starting_at?: number;
    discount?: number | null;
    discount_in?: "%" | "$" | null;
    fixed_price?: number | null;
  };
};

const assignmentToUI = (a: AnyAssignment): VolumeDiscountUI => {
  // console.log(a,"assignmentToUI");
  const t = a.template || {};
  const raw = Number(a.starting_at ?? t.starting_at ?? 1);
  const startingAt = raw <= 0 ? 1 : raw;
  const fixedPrice = a.fixed_price ?? t.fixed_price ?? null;
  const isDiscount = !(fixedPrice != null && Number(fixedPrice) > 0);
  const Discount_id = a.discount_id || "";
  return {
    id: String(a._id || a.template_id || ""),
    templateId: String(a.template_id || t.id || a._id || ""),
    combinationId: a.apply_to_all_variants ? "all" : String(a.variant_id || ""),
    startingAt,
    leaveInitialPrice: isDiscount,
    fixedPrice: isDiscount ? null : Number(fixedPrice || 0),
    discountValue: isDiscount ? Number(a.discount ?? t.discount ?? 0) : null,
    discountIn: isDiscount ? ((a.discount_in ?? t.discount_in) as "%" | "$" | undefined) : undefined,
    discountId: Discount_id
  };
};



const cap = (s?: string) => (s ? s[0].toUpperCase() + s.slice(1) : "");
const getComboId = (c: any): string => String(c?.id ?? c?.variantId ?? c?.variant_id ?? "");

function comboLabel(c?: VolumeDiscountCombination) {
  if (!c) return "Combination";
  if (c.attributes && Object.keys(c.attributes).length) {
    return Object.entries(c.attributes)
      .map(([k, v]) => `${cap(k)}: ${String(v ?? "").trim()}`)
      .join(", ");
  }
  return c.label || (c.id ?? "Combination");
}

const css = `
.vd2-card { border:1px solid #eaecef; border-radius:14px; padding:16px; }
.vd2-head { display:flex; align-items:center; gap:10px; padding:6px 2px 10px; }
.vd2-hicon { display:inline-flex; width:35px; height:35px; border-radius:8px; background:#ffedd4; color:#f54a00; align-items:center; justify-content:center; font-weight:600; }
.vd2-htitle { font-weight:600; font-size:15px; }
.vd2-section { padding:10px 2px; }
.vd2-stitle { display:flex; align-items:center; gap:8px; color:#111827; font-weight:400; margin-bottom:10px;font-size:19px; }
.vd2-stitle-icon { display:inline-flex; width:22px; height:22px; border-radius:7px; align-items:center; justify-content:center; font-size:12px; }
.vd2-stitle-icon.green { background:#dcfce7; color:#00a63e; }
.vd2-stitle-icon.mint { background:#dcfce7; color:#00a63e; }
.vd2-section-icon { display:flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:8px; }
.vd2-label { display:block; font-size:13px; font-weight:600; color:#6b7280; margin-bottom:6px; }
.vd2-sublabel { display:block; font-size:12px; font-weight:600; color:#6b7280; margin:10px 0 6px; }
.vd2-bg { background:#f6f7fb; border-color:#eceff5; }
.vd2-input { width:100%; height:40px; border:1px solid #e5e7eb; border-radius:10px; padding:0 12px; font-size:14px; outline:none; }
.vd2-input:disabled { background:#f3f4f6; color:#9ca3af; }
.vd2-select-wrap { position:relative; }
.vd2-select { width:100%; height:40px; border-radius:10px; border:1px solid #e5e7eb; padding:0 36px 0 12px; font-size:14px; appearance:none; }
.vd2-chevron { position:absolute; right:10px; top:50%; transform:translateY(-50%); pointer-events:none; color:#6b7280; }
.vd2-currency { display:flex; align-items:center; }
.vd2-currency-prefix { display:inline-flex; height:40px; align-items:center; border:1px solid #e5e7eb; border-right:0; border-radius:10px 0 0 10px; padding:0 10px; background:#f6f7fb; color:#6b7280; }
.vd2-chip { background:#f6f7fb; border:1px solid #e5e7eb; color:#6b7280; height:40px; border-radius:10px; padding:0 12px; display:inline-flex; align-items:center; }
.vd2-hr { height:1px; background:#f1f5f9; margin:6px 0 10px; }
.vd2-check { display:flex; align-items:center; gap:8px; font-size:13px; color:#111827; }
.vd2-footer { display:flex; justify-content:flex-end; gap:10px; padding-top:8px; }

/* Table look */
.table td, .table th { vertical-align: middle; }
.table thead th { font-weight:600; color:#6b7280; }
.table tbody td { color:#0f172a; }

/* Modal */
.vd2-modal { position:fixed; inset:0; background:rgba(0,0,0,0.25); display:flex; align-items:center; justify-content:center; padding:12px; z-index:1050; }
.vd2-modal-dialog { width:100%; max-width:560px; background:#fff; border-radius:14px; box-shadow:0 10px 30px rgba(0,0,0,0.15); overflow:hidden; }
.vd2-modal-header { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-bottom:1px solid #f1f5f9; }
.vd2-modal-body { padding:14px 16px; }
.vd2-modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:12px 16px; border-top:1px solid #f1f5f9; }

/* Backdrop & dialog */
.vdm-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.35); display:flex; align-items:center; justify-content:center; padding:12px; z-index:1050; }
.vdm-dialog { width:100%; max-width:560px; background:#fff; border-radius:12px; box-shadow:0 20px 50px rgba(0,0,0,.25); overflow:hidden; }

/* Header */
.vdm-header { display:flex; align-items:center; justify-content:space-between; padding:14px 8px; border-bottom:1px solid #EDF2F7; }
.vdm-title { display:flex; align-items:center; gap:10px; font-weight:600; color:#0F172A; }
.vdm-badge { display:inline-flex; width:28px; height:28px; border-radius:8px; align-items:center; justify-content:center; font-weight:700; }
.vdm-badge-orange { background:#FFF1E7; color:#FF7A00; }
.vdm-badge-purple { background:#F3E8FF; color:#7C3AED; }
.vdm-badge-blue   { background:#E6F0FF; color:#2563EB; }
.vdm-badge-mint   { background:#E8FFF8; color:#11a37f; }
.vdm-close { border:0; background:transparent; font-size:22px; line-height:1; color:#64748B; padding:2px 6px; border-radius:6px; }
.vdm-close:hover { background:#F1F5F9; }

/* Body */
.vdm-body { padding:16px; }
.mb-16 { margin-bottom:16px; }
.mb-8  { margin-bottom:8px; }

.vdm-label-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.vdm-label { font-size:13px; font-weight:600; color:#6B7280; }
.vdm-sublabel { display:block; font-size:12px; font-weight:600; color:#6B7280; margin:10px 0 6px; }

.vdm-select-wrap { position:relative; }
.vdm-select { width:100%; height:40px; border-radius:10px; border:1px solid #E5E7EB; background:#F6F7FB; padding:0 36px 0 12px; font-size:14px; appearance:none; }
.vdm-chevron { position:absolute; right:10px; top:50%; transform:translateY(-50%); pointer-events:none; color:#6B7280; }

.vdm-input { width:100%; height:40px; border:1px solid #E5E7EB; border-radius:10px; padding:0 12px; font-size:14px; background:#F6F7FB; }
.vdm-input:disabled { background:#F3F4F6; color:#9CA3AF; }

.vdm-inline { display:flex; align-items:center; gap:8px; }
.vdm-chip { height:40px; display:inline-flex; align-items:center; padding:0 12px; border:1px solid #E5E7EB; border-radius:10px; background:#F6F7FB; color:#6B7280; }

.vdm-currency { display:flex; align-items:center; }
.vdm-currency-prefix { display:inline-flex; height:40px; align-items:center; border:1px solid #E5E7EB; border-right:0; border-radius:10px 0 0 10px; padding:0 10px; background:#F6F7FB; color:#6B7280; }

.vdm-check { display:flex; align-items:center; gap:8px; font-size:13px; color:#0F172A; }
.vdm-sep { height:1px; background:#F1F5F9; margin:10px 0 12px; }

/* Footer */
.vdm-footer { display:flex; justify-content:flex-end; gap:10px; padding:12px 16px; border-top:1px solid #EDF2F7; }
.vdm-btn { height:38px; padding:0 16px; border-radius:10px; border:1px solid transparent; }
.vdm-btn-light { background:#fff; border-color:#E5E7EB; }
.vdm-btn-light:hover { background:#F8FAFC; }
.vdm-btn-primary { background:#2563EB; color:#fff; }
.vdm-btn-primary:hover { background:#1D4ED8; }
.vd2-invalid { border-color: #ef4444 !important; }
.vd2-error { color: #ef4444; font-size: 12px; margin-top: 6px; }


/* header: desktop side-by-side, mobile stacked */
.vdm-headerbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vdm-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.vdm-filter-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* put next to the CSS you already have in the file */
.vdm-select-disabled:disabled {
  background: #f6f7fb;
  color: #9ca3af;
  cursor: not-allowed;
  border-color: #eceff5;
}

/* stack title and filter on small screens (≤1021px) */
@media (max-width: 1021px) {
  .vdm-headerbar {
    flex-direction: column;
    align-items: stretch;
  }
  .vdm-actions {
    width: 100%;
    justify-content: space-between; /* title already on its own line; actions in next line */
  }
  .vdm-filter-inline {
    width: 100%;
    justify-content: space-between;
  }
  .vdm-filter-inline select {
    flex: 0 1 60%;
    min-width: 0; /* allow shrink on very small screens */
  }
}

.vdm-dup-wrap { margin-top: 10px; }
.vdm-dup {
  display: flex; align-items: center; gap: 12px;
  background: #FFFBEB;               /* pale yellow */
  border: 1px solid #FDE68A;          /* yellow border */
  color: #92400E;                     /* dark amber text */
  border-radius: 10px;
  padding: 12px;
}
.vdm-dup-icon { font-size: 18px; line-height: 1; }
.vdm-dup-title { font-weight: 700; margin-bottom: 4px; }
.vdm-dup-text { font-size: 14px; }
.vdm-dup-actions { margin-left: auto; }

/* Responsive tweaks */
@media (max-width: 575.98px) {
  .vd2-card { padding:12px; border-radius:12px; }
  .vd2-input, .vd2-select { height:38px; }
}
  @media (min-width: 1022px) {
  .vdm-backdrop {
    left: 193px;             /* override the 'left' from inset */
    right: 0;
    top: -22px;
    bottom: 0;
    width: calc(100% - 193px); /* optional: keep the overlay width correct */
  }
}
`;

// ===== Component =====
export default function SignageVolumeDiscount({
  productType,
  combinations,
  value,
  onChange,
  productId,          // ← now in scope
  token,
  simpleVariantId,
}: Props) {
  // console.log(combinations, value, "valueseaea")
  // local shadow of controlled rows, purely UI ops; always mirror to parent
  const [rows, setRows] = React.useState<VolumeDiscountUI[]>(() => value || []);
  const [loading, setLoading] = React.useState(false);
  const [editLoading, setEditLoading] = React.useState(false);
  React.useEffect(() => setRows(value || []), [value]);
  React.useEffect(() => { onChange?.(rows); }, [rows, onChange]);

  // filter: "" = All combinations, "__APPLY_ALL__" = Apply-to-all only, or specific variant id
  const [filterVariantId, setFilterVariantId] = React.useState<string>("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  // add form state
  type FormErrors = { fixedPrice?: string; discountValue?: string; discountIn?: string };
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [dupMsg, setDupMsg] = React.useState<string>("");

  type AddForm = {
    combinationId: "all" | string;
    startingAt: number;
    leaveInitialPrice: boolean;
    fixedPrice?: number | null;
    discountValue?: number | null;
    discountIn?: "%" | "$"; // optional until selected
  };

  const makeNewForm = (): AddForm => ({
    combinationId: "all",
    startingAt: 1,
    leaveInitialPrice: true,     // start with discount mode, not fixed price
    discountValue: 0,
    discountIn: "%",             // valid initial value
    fixedPrice: null,
  });

  const [form, setForm] = useState<AddForm>(makeNewForm());
  // React.useEffect(() => setForm(makeNewForm()), [productType, simpleVariantId, combinations]);

  // edit modal state
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editDraft, setEditDraft] = React.useState<VolumeDiscountUI | null>(null);
  const originalCombinationIdRef = React.useRef<string | null>(null);
  const [editErrors, setEditErrors] = React.useState<FormErrors>({});
  React.useEffect(() => {
    if (!confirmOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [confirmOpen]);

  // ===== Duplicate guard =====
  const hasDuplicate = React.useCallback(
    (combId: "all" | string, startingAt: number, excludeId?: string) => {
      return rows.some(r => {
        if (excludeId && r.id === excludeId) return false;
        if (combId === "all") return r.combinationId === "all" && r.startingAt === startingAt;
        return r.combinationId === combId && r.startingAt === startingAt;
      });
    },
    [rows]
  );

  React.useEffect(() => {
    setDupMsg(hasDuplicate(form.combinationId, form.startingAt) ? "A discount already exists for the same starting quantity." : "");
  }, [form.combinationId, form.startingAt, hasDuplicate]);

  // ===== Validation =====
  const validateAddForm = () => {
    const e: FormErrors = {};
    if (!form.leaveInitialPrice) {
      if (form.fixedPrice == null || Number(form.fixedPrice) <= 0) e.fixedPrice = "Product price is required when not leaving initial price";
    } else {
      if (form.discountValue == null || Number(form.discountValue) <= 0) e.discountValue = "Discount amount is required";
      if (!form.discountIn) e.discountIn = "Discount amount type is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateEditForm = (d: VolumeDiscountUI) => {
    const e: FormErrors = {};
    if (!d.leaveInitialPrice) {
      if (d.fixedPrice == null || Number(d.fixedPrice) <= 0) e.fixedPrice = "Product price is required when not leaving initial price";
    } else {
      if (d.discountValue == null || Number(d.discountValue) <= 0) e.discountValue = "Discount amount is required";
      if (!d.discountIn) e.discountIn = "Discount amount type is required";
    }
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  // ADD near the top (imports already present in your file):
  // import { getAssignmentsDatatable } from "./volumeDiscountTemplates";

  // Helper: map API assignment → UI row (you already have assignmentToUI; keep it)
  const loadRows = React.useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const body: AssignmentsDatatableQuery = {
        page: 1,
        limit: 200,
        status: "active",
        sort: { field: "created_at", order: "desc" },
      };

      const { rows: apiRows } = await getAssignmentsDatatable(
        String(productId),
        body,
        (typeof token === "string" ? token : null)
      );

      const ui = (apiRows || []).map(assignmentToUI);
      setRows(ui);
      onChange?.(ui);
    } catch (e: any) {
      console.error("loadRows error:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [productId, token, onChange]);

  // Load on mount / when product changes
  React.useEffect(() => {
    loadRows();
  }, [loadRows, productId]);


  const onAdd = async () => {
    if (!productId) { showError("Product not saved yet."); return; }
    if (!form.startingAt || form.startingAt < 1) { showError("Starting at must be ≥ 1"); return; }

    const isVariable = productType === "variable";
    const tokenStr = (typeof token === "string" ? token : null);

    // target combination/variant
    const uiCombId = form.combinationId; // "all" or variant id (for variable)
    const targetCombId = isVariable
      ? String(uiCombId || "")
      : String(simpleVariantId || ""); // simple → must target the single variant

    const applyAll = isVariable && uiCombId === "all";

    // basic validation per mode
    if (form.leaveInitialPrice) {
      // discount mode
      if (!form.discountValue || Number(form.discountValue) <= 0) {
        showError("Enter a discount value greater than 0.");
        return;
      }
      if (!form.discountIn) {
        showError("Select discount type (% or $).");
        return;
      }
    } else {
      // fixed price mode
      if (form.fixedPrice == null || Number(form.fixedPrice) <= 0) {
        showError("Enter a fixed price greater than 0.");
        return;
      }
    }

    // duplicate guard (same startingAt for same scope)
    const dup = rows.some(r =>
      r.startingAt === form.startingAt && (
        applyAll
          ? r.combinationId === "all" // when applying to all, only clash with other 'all' at same qty
          : (r.combinationId === "all" || r.combinationId === targetCombId) // clash with 'all' or same variant
      )
    );
    if (dup) { showError("A discount already exists at this starting quantity."); return; }

    setLoading(true);
    try {
      // Display name
      const name = form.leaveInitialPrice
        ? `${Number(form.discountValue || 0)}${form.discountIn || "%"} off ${Number(form.startingAt)}+`
        : `Fixed price at ${Number(form.startingAt)}+`;

      // --- base payload (typed) ---
      const baseTpl: VolumeTemplateCreatePayload = {
        name,
        starting_at: Number(form.startingAt),
        discount: Number(form.leaveInitialPrice ? form.discountValue || 0 : 0),
        discount_in: (form.leaveInitialPrice ? (form.discountIn === "$" ? "$" : "%") : "%"),
        status: "active",
        priority: 100,
      };

      // --- extend for fixed mode (backend expects fixed_price and NO discount fields) ---
      const tplPayload: any = { ...baseTpl };
      if (!form.leaveInitialPrice) {
        tplPayload.fixed_price = Number(form.fixedPrice || 0);
        delete tplPayload.discount;
        delete tplPayload.discount_in;
      }

      // 1) Create template
      let template;
      try {
        template = await createVolumeDiscountTemplate(tplPayload, tokenStr);
        // console.log("createVolumeDiscountTemplate response:", template);  
      } catch (e: any) {
        const msg = e?.payload?.message || e?.payload?.error || e?.message || "Failed to create template";
        console.error("createVolumeDiscountTemplate error:", e?.status, e?.payload);
        showError(msg);
        setLoading(false);
        return;
      }

      // 2) Create assignment
      await createVolumeDiscountAssignment(
        String(template._id),
        {
          product_id: String(productId),
          variant_id: isVariable
            ? (applyAll ? null : targetCombId)
            : targetCombId,              // simple → must send concrete variant id
          apply_to_all_variants: isVariable ? applyAll : false,
          status: "active" as const,
        },
        tokenStr
      );

      // 3) Update UI table
      const newRow: VolumeDiscountUI = {
        id: `vd_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        combinationId: isVariable ? (applyAll ? "all" : targetCombId) : targetCombId,
        startingAt: Number(form.startingAt),
        leaveInitialPrice: !!form.leaveInitialPrice,
        fixedPrice: form.leaveInitialPrice ? null : Number(form.fixedPrice || 0),
        discountValue: form.leaveInitialPrice ? Number(form.discountValue || 0) : null,
        discountIn: form.leaveInitialPrice ? (form.discountIn as ("%" | "$")) : undefined,
        templateId: String(template._id),
      };

      setRows(prev => {
        const next = [newRow, ...prev].sort((a, b) => a.startingAt - b.startingAt);
        onChange?.(next);
        return next;
      });

      setForm(makeNewForm());
      setErrors({});
      showSuccess("Volume discount added.");
      await loadRows();
    } catch (err: any) {
      console.error(err);
      showError(err?.message || "Failed to add volume discount");
    } finally {
      setLoading(false);
    }
  };



  const startEdit = (r: VolumeDiscountUI) => {
    setEditDraft({ ...r });
    originalCombinationIdRef.current = r.combinationId;
    setEditErrors({});
    setIsEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editDraft) return;

    // keep the same scope; we only edit the rule values here
    if (hasDuplicate(editDraft.combinationId, editDraft.startingAt, editDraft.id)) {
      showError("A discount already exists at this starting quantity.");
      return;
    }
    if (!validateEditForm(editDraft)) return;

    setEditLoading(true);
    try {
      const isVariable = productType === "variable";

      // Build PATCH for the template only (discount/fixed price, starting_at). Do not call volume-discount-assignments PATCH.
      const patch: any = {
        starting_at: Number(editDraft.startingAt || 1),
        status: "active" as const,
      };

      if (editDraft.leaveInitialPrice) {
        patch.fixed_price = null;
        patch.discount = Number(editDraft.discountValue || 0);
        patch.discount_in = editDraft.discountIn === "$" ? "$" : "%";
      } else {
        patch.fixed_price = Number(editDraft.fixedPrice || 0);
        patch.discount = null;
        patch.discount_in = undefined;
      }

      await updateVolumeDiscountTemplates(
        String(editDraft.discountId),
        patch,
        (typeof token === "string" ? token : null)
      );

      const combinationChanged =
        productType === "variable" &&
        originalCombinationIdRef.current !== null &&
        editDraft.combinationId !== originalCombinationIdRef.current;

      if (combinationChanged) {
        const isApplyAll = editDraft.combinationId === "all";
        const Assignmentpatch = {
          variant_id: isApplyAll ? null : editDraft.combinationId,
          apply_to_all_variants: isApplyAll,
        };
        await updateVolumeDiscountAssignment(
          String(editDraft.id),
          Assignmentpatch,
          (typeof token === "string" ? token : null)
        );
      }

      showSuccess("Volume discount updated.");

      setIsEditOpen(false);
      setEditDraft(null);
      originalCombinationIdRef.current = null;
      setEditErrors({});
      await loadRows();
    } catch (e: any) {
      showError(e?.message || "Failed to save changes");
    } finally {
      setEditLoading(false);
    }
  };


  const onDeleteRow = async (r: VolumeDiscountUI) => {
    try {
      setLoading(true);
      await deleteVolumeDiscountAssignment(String(r.id), (typeof token === "string" ? token : null));
      showSuccess("Volume discount deleted.");
      await loadRows();
    } catch (e: any) {
      console.error("delete error:", e?.message || e);
      showError(e?.message || "Failed to delete volume discount");
    } finally {
      setLoading(false);
    }
  };


  const rowsForTable = React.useMemo(() => {
    if (productType === "variable") {
      if (filterVariantId === "__APPLY_ALL__") {
        return rows.filter(r => r.combinationId === "all");
      }
      if (filterVariantId && filterVariantId !== "__APPLY_ALL__") {
        return rows.filter(r => r.combinationId === filterVariantId);
      }
      return rows.slice(); // all
    }
    // simple product → show all rows (combination column is hidden by table columns)
    return rows.slice();
  }, [rows, productType, filterVariantId]);


  // ===== Table =====
  type RowVM = {
    id: string;
    combination?: string;
    fixed: string;
    discount: string;
    in: string;
    startingAt: number;
  };

  const fmtMoney = (n?: number | null) => typeof n === "number" && !Number.isNaN(n) ? `$${n.toFixed(2)}` : "--";

  const vmData: RowVM[] = React.useMemo(() => {
    const isVariable = productType === "variable";

    return rowsForTable.map((r) => {
      // combination label (only for variable products)
      let combText: string | undefined = undefined;
      if (isVariable) {
        if (r.combinationId === "all") {
          combText = "Apply to all combinations";
        } else {
          const combo = (combinations || []).find(
            (c) => getComboId(c) === r.combinationId
          );
          combText = comboLabel(combo);
        }
      }

      return {
        id: r.id,
        combination: combText,
        fixed: !r.leaveInitialPrice ? fmtMoney(r.fixedPrice) : "--",
        discount: r.leaveInitialPrice ? String(r.discountValue ?? "--") : "--",
        in: r.leaveInitialPrice ? (r.discountIn ?? "--") : "--",
        startingAt: r.startingAt,
      };
    });
  }, [rowsForTable, productType, combinations]);


  const idToOriginal = React.useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);

  const colHelper = createColumnHelper<RowVM>();
  const columns = useMemo<ColumnDef<RowVM, any>[]>(() => [
    ...(productType === "variable"
      ? [
        colHelper.accessor("combination", {
          id: "combination",
          header: () => <span className="th-text text-center d-inline-block w-100">Combination</span>,
          cell: (ctx) => <div className="text-sm text-center">{ctx.getValue() || "All combinations"}</div>,
        }),
      ]
      : []),
    colHelper.accessor("fixed", {
      id: "fixed",
      header: () => <span className="th-text text-center d-inline-block w-100">Fixed price</span>,
      cell: (ctx) => <div className="text-sm text-center">{ctx.getValue()}</div>,
    }),
    colHelper.accessor("discount", {
      id: "discount",
      header: () => <span className="th-text text-center d-inline-block w-100">Apply a discount of</span>,
      cell: (ctx) => <div className="text-sm text-center">{ctx.getValue()}</div>,
    }),
    colHelper.accessor("in", {
      id: "in",
      header: () => <span className="th-text text-center d-inline-block w-100">Discount in</span>,
      cell: (ctx) => <div className="text-sm text-center">{ctx.getValue()}</div>,
    }),
    colHelper.accessor("startingAt", {
      id: "startingAt",
      header: () => <span className="th-text text-center d-inline-block w-100">Starting at</span>,
      cell: (ctx) => <div className="text-sm text-center">{ctx.getValue()}</div>,
    }),
    {
      id: "actions",
      header: () => <span className="th-text text-center d-inline-block w-100">Actions</span>,
      cell: ({ row }) => {
        const original = idToOriginal.get(row.original.id)!;
        return (
          <div className="text-sm text-center">
            <div className="d-inline-flex align-items-center gap-2">
              <EditIconButton onClick={() => startEdit(original)} />
              <DeleteIconButton onClick={() => onDeleteRow(original)} />
            </div>
          </div>
        );
      },
    },
  ], [productType, idToOriginal]);

  const table = useReactTable({
    data: vmData,                  // already memoized
    columns,                       // now memoized
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    autoResetPageIndex: false,     // ✅ stop bounce
    autoResetAll: false,           // optional
  });


  // ===== Render =====
  const handleCancelAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); e.stopPropagation();
    setErrors({}); setDupMsg(""); setForm(makeNewForm());
    (document.activeElement as HTMLElement | null)?.blur();
  };

  const editDiscountInValue = (editDraft?.discountIn ?? "") as string;

  // Human label for the currently targeted combination
  const getComboLabel = React.useCallback(
    (combId: string | "all" | undefined) => {
      if (productType === "simple") return "this product";

      if (!combId || combId === "all") return "Apply to all combinations";

      // combinations list: [{ id, attributes, variant_name, sku }]
      const c = (combinations || []).find(
        (x) => String(x.id) === String(combId)
      );
      if (!c || !c.attributes) return "this combination";

      const parts = Object.entries(c.attributes).map(([k, v]) => {
        const key = String(k || "").replace(/_/g, " ");
        const titled = key.charAt(0).toUpperCase() + key.slice(1);
        return `${titled}: ${v as any}`;
      });

      return parts.length ? parts.join(", ") : "this combination";
    },
    [productType, combinations]
  );

  // Scope-aware duplicate check
  const isDuplicateAt = React.useCallback(
    (startAt: number) => {
      // rows = your current discount rules list
      return rows.some((r) => {
        if (productType === "variable") {
          const scope = form.combinationId || "all";
          if (scope === "all") {
            // "all" only clashes with another "all" at same qty
            return r.combinationId === "all" && Number(r.startingAt) === startAt;
          }
          // specific combo clashes with either an "all" or the same combo
          return (
            Number(r.startingAt) === startAt &&
            (r.combinationId === "all" || String(r.combinationId) === String(scope))
          );
        }
        // simple product: any rule at the same qty is a duplicate
        return Number(r.startingAt) === startAt;
      });
    },
    [rows, productType, form.combinationId]
  );

  React.useEffect(() => {
    const startAt = Number(form.startingAt || 0);
    // Don’t show anything if startingAt not set yet or (variable & no scope yet)
    if (!startAt || (productType === "variable" && !form.combinationId)) {
      setDupMsg("");
      return;
    }

    if (isDuplicateAt(startAt)) {
      const label = getComboLabel(
        productType === "simple" ? (simpleVariantId as any) : form.combinationId
      );
      setDupMsg(
        `A volume discount already exists for ${label} starting at ${startAt} unit(s). Please choose a different starting quantity for this combination.`
      );
    } else {
      setDupMsg("");
    }
  }, [
    form.startingAt,
    form.combinationId,
    productType,
    combinations,
    simpleVariantId,
    isDuplicateAt,
    getComboLabel,
  ]);

  return (
    <div>
      <style>{css}</style>

      {/* ===== Add form ===== */}
      <div className="container px-0">
        <div className="row d-flex justify-content-center">
          <div className="col-md-9 col-lg-9 col-xl-9">
            <div className="vd2-card box-shadow">
              <div className="vd2-head mb-4">
                <span className="fs-20 vd2-hicon">%</span>
                <span className="vd2-htitle fs-17 fw-normal">Add Volume Discount</span>
              </div>

              <div className="vd2-section">
                <div className="vd2-stitle mb-5">
                  <div className="vd2-section-icon" style={{ background: "#dcfce7" }}>
                    <Package size={16} style={{ color: "#00a63e" }} />
                  </div>
                  <span className="fs-16 fw-medium">Specific price conditions</span>
                </div>

                {productType === "variable" && (
                  <div className="mb-16 mb-4">
                    <div className="d-flex align-items-center gap-2 wrap-anywhere mb-2">
                      <div {...iconLabelProps(19)}>
                        <Package {...withIconStyle("#9810fa", 19)} />
                      </div>
                      <span className="fs-14 fw-medium">Combinations</span>
                    </div>
                    <div className="vd2-select-wrap">
                      <select
                        className="vd2-select vd2-bg"
                        value={form.combinationId}
                        onChange={(e) => setForm((p) => ({ ...p, combinationId: e.target.value as any }))}
                      >
                        <option value="">Select combination...</option>
                        <option value="all">Apply to all combinations</option>
                        {(combinations || []).map((c, i) => (
                          <option key={c.id || i} value={c.id || String(i)}>
                            {comboLabel(c)}
                          </option>
                        ))}
                      </select>
                      <span className="vd2-chevron">▾</span>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-lg-6">
                    <div className="d-flex align-items-center gap-2 wrap-anywhere mb-2">
                      <div {...iconLabelProps(19)}>
                        <Package {...withIconStyle("#155dfc", 19)} />
                      </div>
                      <span className="fs-14 fw-medium">Starting at</span>
                    </div>
                    <div className="d-flex gap-12 align-items-center gap-3">
                      <input
                        type="number"
                        min={1}
                        className="vd2-input vd2-bg"
                        placeholder="1"
                        value={Number(form.startingAt ?? 0) <= 0 ? "" : form.startingAt}
                        onChange={(e) => {
                          const raw = e.target.value;

                          // empty => show placeholder 1 (don't display 0)
                          if (raw === "" || raw === null) {
                            setForm((p) => ({ ...p, startingAt: 0 }));
                            return;
                          }

                          const n = Number(raw);
                          setForm((p) => ({ ...p, startingAt: Number.isFinite(n) ? Math.max(1, n) : 0 }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                      />

                      <div className="vd2-chip">Unit(s)</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="vd2-hr my-3" />

              <div className="vd2-section">
                <div className="vd2-stitle mb-4">
                  <div className="vd2-section-icon" style={{ background: "#d0fae5" }}>
                    <span style={{ color: "#009966", fontSize: "16px", fontWeight: "600" }}>$</span>
                  </div>
                  <span className="fs-16 fw-medium">Impact on price</span>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <label className="vd2-sublabel">Change Product Price (tax excl.)</label>
                    <div className="vd2-currency">
                      <span className="vd2-currency-prefix">$</span>
                      <input
                        type="number"
                        className={`vd2-input rounded-start-0 ${!form.leaveInitialPrice && errors.fixedPrice ? "vd2-invalid" : ""
                          }`}
                        placeholder="0"
                        disabled={form.leaveInitialPrice}
                        value={form.fixedPrice == 0 ? "" : form.fixedPrice ?? ""}
                        onChange={(e) => setForm((p) => ({ ...p, fixedPrice: Number(e.target.value) || 0 }))}
                      />
                    </div>
                    {!form.leaveInitialPrice && errors.fixedPrice && (
                      <div className="vd2-error">{errors.fixedPrice}</div>
                    )}
                  </div>
                </div>

                <label className="vd2-check mt-3">
                  <input
                    type="checkbox"
                    checked={form.leaveInitialPrice}
                    onChange={(e) => {
                      const leave = e.target.checked;
                      setForm((p) =>
                        leave
                          // Discount mode (leave initial price): clear fixed price, keep discount fields
                          ? { ...p, leaveInitialPrice: true, fixedPrice: null, discountValue: p.discountValue ?? 0, discountIn: p.discountIn ?? "%" }
                          // Fixed price mode: reset discount fields to undefined, set fixed price to 0
                          : { ...p, leaveInitialPrice: false, discountValue: undefined, discountIn: undefined, fixedPrice: 0 }
                      );
                      setErrors({});
                    }}
                  />
                  <span>Leave initial price</span>
                </label>

                {form.leaveInitialPrice && (
                  <div className="row g-3 mt-1">
                    <div className="col-12 col-md-6">
                      <label className="vd2-sublabel">Apply a discount of</label>
                      <input
                        type="number"
                        className={`vd2-input vd2-bg ${form.leaveInitialPrice && errors.discountValue ? "vd2-invalid" : ""
                          }`}
                        disabled={!form.leaveInitialPrice}
                        value={form.discountValue == 0 ? "" : form.discountValue ?? ""}
                        onChange={(e) => setForm((p) => ({ ...p, discountValue: Number(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                      {form.leaveInitialPrice && errors.discountValue && (
                        <div className="vd2-error">{errors.discountValue}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="vd2-sublabel">Discount in</label>
                      <div className="vd2-select-wrap">
                        <select
                          className={`vd2-select vd2-bg ${form.leaveInitialPrice && errors.discountIn ? "vd2-invalid" : ""}`}
                          disabled={!form.leaveInitialPrice}
                          value={form.discountIn ?? ""}             // UI can show "", but we won't store "" in state
                          onChange={(e) => {
                            const raw = e.target.value as "" | "%" | "$";
                            setForm((p) => ({
                              ...p,
                              discountIn: raw === "" ? undefined : raw, // ← never persist ""
                            }));
                          }}
                        >
                          <option value="">Select amount type</option>
                          <option value="%">% (Percentage)</option>
                          <option value="$">$ (Dollars)</option>
                        </select>

                        <span className="vd2-chevron">▾</span>
                      </div>
                      {form.leaveInitialPrice && errors.discountIn && (
                        <div className="vd2-error">{errors.discountIn}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* {dupMsg && <div className="alert alert-warning mt-3 small">{dupMsg}</div>} */}

              <div className="col-12 mb-2">
                <hr />
              </div>

              {/* footer */}
              <div className="d-flex justify-content-end align-items-center ms-1" style={{ padding: "14px 18px" }}>
                <button
                  type="button"
                  onClick={handleCancelAdd}
                  className="btn back-btn surface-btn fw-bold fs-16 me-2"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!validateAddForm()) return;     // your validation
                    if (dupMsg) return;                 // do not open modal if duplicate
                    setConfirmOpen(true);               // 👉 open confirm modal
                  }}
                  disabled={!!dupMsg || loading}
                >
                  {loading ? "Saving..." : "Add"}
                </button>

              </div>

              {confirmOpen && (
                <div className="vdm-backdrop" onMouseDown={() => setConfirmOpen(false)}>
                  <div className="vdm-dialog" onMouseDown={(e) => e.stopPropagation()}>
                    <div className="vdm-header">
                      <div className="vdm-title">
                        <span className="vdm-badge vdm-badge-blue">?</span>
                        <span>Confirm Add Volume Discount</span>
                      </div>
                      <button
                        className="vdm-close"
                        onClick={() => setConfirmOpen(false)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    <div className="vdm-body">
                      Are you sure you want to add this volume discount? This will create a new pricing
                      rule for the selected combination.
                    </div>

                    <div className="vdm-footer">
                      <button
                        type="button"
                        className="vdm-btn vdm-btn-light"
                        onClick={() => setConfirmOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="vdm-btn vdm-btn-primary"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setConfirmOpen(false);
                          if (!validateAddForm()) return;     // your validation
                          if (dupMsg) return;
                          await onAdd(); // 👉 only now hit API
                        }}
                      >
                        Add Discount
                      </button>
                    </div>
                  </div>
                </div>
              )}


            </div>

            {dupMsg && (
              <div className="vdm-dup-wrap">
                <div className="vdm-dup">
                  <div className="vdm-dup-icon">⚠️</div>
                  <div className="vdm-dup-content">
                    <div className="vdm-dup-title">Duplicate Volume Discount</div>
                    <div className="vdm-dup-text">
                      A volume discount already exists for <strong>
                        {form.combinationId === "all" ? "Apply to all combinations" : "this combination"}
                      </strong>{" "}
                      starting at <strong>{Number(form.startingAt)} unit(s)</strong>. Please choose a different starting
                      quantity for this combination.
                    </div>
                  </div>
                  <div className="vdm-dup-actions">
                    <button type="button" className="btn btn-light btn-sm" onClick={() => setDupMsg("")}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="mt-4 card overflow-hidden box-shadow-datatables shadow-border-datatables">
        <div className="vdm-headerbar bg-white border-bottom px-3 px-md-4 py-3">
          <h6 className="mb-0">Volume Discounts {loading ? "• Loading…" : ""}</h6>

          <div className="vdm-actions">
            {productType === "variable" && (
              <div className="vdm-filter-inline">
                <span className="d-inline-flex align-items-center text-muted small">
                  {/* filter icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
                    <path d="M3 5h18M6 12h12M10 19h4" stroke="#64748B" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Filter by combination:
                </span>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 220 }}
                  value={filterVariantId}
                  onChange={(e) => setFilterVariantId(e.target.value)}
                >
                  {/* All = empty string */}
                  <option value="">All combinations</option>

                  {/* NEW: special value for "Apply to all combinations" */}
                  <option value="__APPLY_ALL__">Apply to all combinations</option>

                  {(combinations || []).map((c) => {
                    const vid = getComboId(c);
                    return (
                      <option key={vid} value={vid}>
                        {comboLabel(c)}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {/* 
    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadRows} disabled={loading}>
      Refresh
    </button> */}
          </div>
        </div>

        <div className="rdt-desktop shadow-sm">
          <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>
      </div>

      <div className="rdt-mobile">
        <MobileCardsOnly
          table={table}
          idColId={productType === "variable" ? "combination" : undefined}
          actionColId="actions"
          omitMobile={[]}
        />
      </div>

      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      {isEditOpen && editDraft && (
        <div
          className="vdm-backdrop"
          onMouseDown={() => {
            setIsEditOpen(false);
            setEditDraft(null);
            originalCombinationIdRef.current = null;
            setEditErrors({});
          }}
        >
          <div className="vdm-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="vdm-header">
              <div className="vdm-header">
                <div className="d-flex align-items-center gap-2 wrap-anywhere mb-2">
                  <div {...iconLabelProps(19)}>
                    <SquarePen {...withIconStyle("#f54a00", 19)} />
                  </div>
                  <span className="order-card-cells">Edit Volume Discount</span>
                </div>
              </div>
              <button
                className="vdm-close"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditDraft(null);
                  originalCombinationIdRef.current = null;
                  setEditErrors({});
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="vdm-body">
              {/* Edit Modal – Combinations (variable only) */}
              {productType === "variable" && (
                <div className="mb-16">
                  <div className="d-flex align-items-center gap-2 wrap-anywhere mb-2">
                    <div {...iconLabelProps(19)}>
                      <Package {...withIconStyle("#9810fa", 19)} />
                    </div>
                    <span className="order-card-cells">Combinations</span>
                  </div>

                  <div className="vdm-select-wrap">
                    <select
                      className="vdm-select"
                      value={editDraft.combinationId}
                      onChange={(e) => {
                        setEditDraft({ ...editDraft, combinationId: e.target.value as "all" | string });
                      }}
                    >
                      <option value="all">Apply to all combinations</option>
                      {(combinations || []).map((c, i) => (
                        <option key={c.id || i} value={c.id || String(i)}>
                          {comboLabel(c)}
                        </option>
                      ))}
                    </select>
                    <span className="vdm-chevron">▾</span>
                  </div>
                </div>
              )}


              <div className="mb-16">
                <div className="d-flex align-items-center gap-2 wrap-anywhere mb-2">
                  <div {...iconLabelProps(19)}>
                    <Package {...withIconStyle("#155dfc", 19)} />
                  </div>
                  <span className="order-card-cells">Starting at</span>
                </div>
                <div className="vdm-inline">
                  <input
                    type="number"
                    min={1}
                    className="vdm-input"
                    placeholder="1"
                    value={Number(editDraft.startingAt ?? 0) <= 0 ? "" : editDraft.startingAt}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "" || raw === null) {
                        setEditDraft({ ...editDraft, startingAt: 0 });
                        return;
                      }
                      const n = Number(raw);
                      setEditDraft({ ...editDraft, startingAt: Number.isFinite(n) ? Math.max(1, n) : 0 });
                    }}
                  />
                  <span className="vdm-chip">Unit(s)</span>
                </div>
              </div>

              <div className="vdm-sep" />
              <div className="mb-8">
                <label className="vd2-sublabel">Change Product Price (tax excl.)</label>
                <div className="vd2-currency">
                  <span className="vd2-currency-prefix">$</span>
                  <input
                    type="number"
                    className={`vd2-input rounded-start-0 ${!editDraft.leaveInitialPrice && editErrors.fixedPrice ? "vd2-invalid" : ""
                      }`}
                    placeholder="0"
                    disabled={editDraft.leaveInitialPrice}
                    value={editDraft.fixedPrice == 0 ? "" : editDraft.fixedPrice ?? ""}
                    onChange={(e) => setEditDraft({ ...editDraft, fixedPrice: Number(e.target.value) || 0 })}
                  />
                </div>
                {!editDraft.leaveInitialPrice && editErrors.fixedPrice && (
                  <div className="vd2-error">{editErrors.fixedPrice}</div>
                )}

                <label className="vdm-check mt-3">
                  <input
                    type="checkbox"
                    checked={editDraft.leaveInitialPrice}
                    onChange={(e) => {
                      const leave = e.target.checked;
                      setEditErrors({});
                      setEditDraft((prev) => {
                        if (!prev) return prev;
                        return leave
                          // discount mode: clear fixed price, keep discount fields (and default discountIn if empty)
                          ? {
                            ...prev,
                            leaveInitialPrice: true,
                            fixedPrice: null,
                            discountValue: prev.discountValue ?? 0,
                            discountIn: (prev.discountIn as "%" | "$" | undefined) ?? "%",
                          }
                          // fixed price mode: clear discount fields, set fixed price to 0
                          : {
                            ...prev,
                            leaveInitialPrice: false,
                            discountValue: null,
                            discountIn: undefined,
                            fixedPrice: 0,
                          };
                      });
                    }}
                  />
                  <span>Leave initial price</span>
                </label>


                {editDraft.leaveInitialPrice && (
                  <div className="row g-3 mt-1">
                    <div className="col-12 col-md-6">
                      <label className="vd2-sublabel">Apply a discount of</label>
                      <input
                        type="number"
                        className={`vd2-input vd2-bg ${editDraft.leaveInitialPrice && editErrors.discountValue ? "vd2-invalid" : ""
                          }`}
                        value={editDraft.discountValue == 0 ? "" : editDraft.discountValue ?? ""}
                        onChange={(e) => setEditDraft({ ...editDraft, discountValue: Number(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      {editDraft.leaveInitialPrice && editErrors.discountValue && (
                        <div className="vd2-error">{editErrors.discountValue}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="vd2-sublabel">Discount in</label>
                      <div className="vd2-select-wrap">
                        <select
                          className={`vd2-select vd2-bg ${editDraft?.leaveInitialPrice && editErrors.discountIn ? "vd2-invalid" : ""
                            }`}
                          value={editDiscountInValue} // "" | "%" | "$" for UI only
                          onChange={(e) => {
                            const raw = e.target.value as "" | "%" | "$";
                            setEditDraft((prev) => {
                              if (!prev) return prev;
                              return raw === "" ? { ...prev, discountIn: undefined } : { ...prev, discountIn: raw };
                            });
                          }}
                        >
                          <option value="">Select amount type</option>
                          <option value="%">% (Percentage)</option>
                          <option value="$">$ (Dollars)</option>
                        </select>
                        <span className="vd2-chevron">▾</span>
                      </div>
                      {editDraft.leaveInitialPrice && editErrors.discountIn && (
                        <div className="vd2-error">{editErrors.discountIn}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="vdm-footer">
              <button
                type="button"
                className="vdm-btn vdm-btn-light"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditDraft(null);
                  originalCombinationIdRef.current = null;
                  setEditErrors({});
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="vdm-btn vdm-btn-primary"
                disabled={editLoading}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  saveEdit();
                }}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
